import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { freeId } from '../domain/freeId';
import {
  clearSelectionKey, composeRecipients, composeWords, myselfRecipient, sendAtRefusal,
  teacherMessageId, teacherMessageWrite,
} from '../domain/notifications';
import { applyMessageTemplate, attachmentRefusal, composeWord } from '../domain/compose';
import { instantOfWallClockIn, nextWholeHourIn } from '../domain/timeZone';
import useMessageBodyEdits from './useMessageBodyEdits';

// Message History's New Message ▾ (#522) and a row's Forward (#546): who it
// goes to, what the teacher typed, and the send.
//
// One Send is one message. The message's id comes from a fresh id made when
// the composer opens (`freeId`, a genuine append), so pressing Send twice
// writes one document twice; opening the composer again is a new message.
// What may be sent, and the document, are the core's (`compose_recipients`,
// `compose_refusal`, `teacher_message_request`); `send` is the provider's
// `sendTeacherMessage`, which queues it on the outbox like every other
// message.
//
// MMS's composer (#547, #599, #600): From is the studio's address or the
// teacher's own (`fromChoices`; it opens on the first). To is the chosen
// people as removable chips with a `Select...` menu that adds more — this
// student's entries, then the studio-wide groups (Active Students, Trial
// Parents, `<teacher>'s` …, with "Show all" adding inactive, waiting and lead)
// and Clear Selection, which empties To. Extra Recipients takes addresses
// "not in" the app, separated by a semicolon or the Enter key; CC me copies
// the teacher. Send Email is Immediately or "Automatically at a future
// date/time", a date and a time on the profile's clock (`timeZone`: the
// family's, else the studio's), opening on today at the next whole hour.
// Forward opens the same composer as "Forward Email" with the subject and the
// body filled in and nobody on To. Several recipients are sent one email
// each, MMS's "automatically BCC'ed" (functions/delivery/transport/sendgrid.js).
//
// The message's editing (#596, #597, #598) is the core's (core/src/compose.rs,
// which says what of MMS's each keeps): the toolbar writes Markdown around the
// selection (`format`); a picked template is appended to the message and its
// subject, when it has one, replaces the subject (`pickTemplate`, #605); the
// placeholder menu writes MMS's `%FirstName%`-style token at the cursor
// (`insertPlaceholder`, #604; core/src/placeholders.rs), filled per recipient
// when sent; Insert Image puts a library picture on a line of its own.
//
// Attach Files (#605) sends real attachments, as MMS's does: each pick
// (`attachFile`, one file, any type) is checked by the core
// (`attachmentRefusal`: 20 MB a file and 20 MB in all, no program or script)
// and uploaded at once through `upload` — src/data/messageAttachments.js,
// handed in so this holds no Firebase — under this message's id, where it
// waits in Storage until the message is sent. Send waits for every upload.
// Removing a file (`detachFile`) and closing without sending (`close`) cancel
// an upload still going and delete what was uploaded, through `discard`; the
// sweep removes whatever that misses. A failed upload stays on the message
// with its sentence and Try again (`retryFile`, #612), and Send refuses with
// `attach_not_uploaded` until it is retried or removed. A stalled upload is
// such a failure (src/lib/uploadWatch.js). The message names each file as
// `{cap}/{name}` (core/src/compose.rs says why), and the sender attaches the
// bytes. Forward carries none: the files belong to the message they were sent
// with and are deleted a week after it went, so the teacher attaches again.
// What MMS's Forward does with attachments is unchecked (#605).
//
// `myEmail` is the address the teacher signed in with: CC me's "Myself", and
// no CC me without one.
//
// `reachedFor(to)` names the students a message to `to` reached (#607,
// `reachedStudents`), which the message records so it is listed on each one's
// Message History, not only on the tab it was written from.

const CLOSED = {
  active: false, title: null, seed: null, to: [], extra: '', ccMe: false,
  subject: '', body: '', attachments: [], error: null, notice: null, sending: false,
  from: null, later: false, date: '', time: '', showAll: false,
};

// One file on the message: `key` this composer's own; `status` `uploading`
// (with `transferred` of `size` bytes so far), `ready`, when `entry` and
// `path` are the server's, or `failed`, with the sentence in `message`.
let fileKeys = 0;

const settledUploads = (list) => list.filter((a) => a.status === 'ready');

export default function useNewMessage({
  studentId = null, familyId = null, send = null, myEmail = null, fromChoices = [], timeZone = null,
  now = () => Date.now(), reachedFor = null, upload = null, discard = null,
} = {}) {
  const [state, setState] = useState(CLOSED);
  const firstFrom = fromChoices[0]?.key ?? null;
  // The composer as last drawn, for an upload that finishes after it closed
  // or after its file was removed: the bytes are then not the message's and
  // are deleted rather than added back.
  const current = useRef(state);
  useEffect(() => { current.current = state; }, [state]);
  // Each file's bytes, for Try again, and each upload's AbortController, by
  // the file's key — held outside the state, which is what is drawn.
  const picked = useRef(new Map());
  const going = useRef(new Map());
  const forget = (key) => {
    picked.current.delete(key);
    const controller = going.current.get(key);
    going.current.delete(key);
    if (controller) controller.abort();
  };

  // What every composer opens with: From on its first entry, and Send
  // Email's date and time on today's next whole hour, ready for "later".
  const opened = useCallback((over) => {
    const next = nextWholeHourIn(now(), timeZone);
    return {
      ...CLOSED, active: true, seed: freeId('msg'), from: firstFrom, date: next?.date || '', time: next?.time || '', ...over,
    };
  }, [firstFrom, timeZone, now]);

  // New Message ▾'s entry: its people on To.
  const open = useCallback((choice) => {
    setState(opened({ title: choice?.label || null, to: [...(choice?.to || [])] }));
  }, [opened]);

  // A row's Forward: its subject and its words, nobody on To, no files (the
  // header says why). A teacher's own message forwards its subject line;
  // any other kind the subject the sender kept with its words (#595).
  const forward = useCallback((row) => {
    setState(opened({
      title: composeWords().forwardTitle,
      subject: row?.forwardSubject || row?.subject || '',
      body: row?.body || '',
    }));
  }, [opened]);

  // Closed without sending: the draft is abandoned, and so are its uploads —
  // unless Send is under way, when they are the message's.
  const close = useCallback(() => {
    const { attachments: left = [], sending } = current.current;
    setState(CLOSED);
    if (sending) return;
    for (const a of left) forget(a.key);
    if (discard) for (const a of left) if (a.path) discard(a.path);
  }, [discard]);

  const change = useCallback((patch) => setState((s) => ({
    ...s, ...patch, error: null, notice: null,
  })), []);

  // The toolbar, the placeholder menu and Insert Image (#596, #604, #531):
  // the core's edits over the box's selection, each answering with the
  // selection the drawer puts back, or null when refused — then the message
  // is as it was and the notice says why. useMessageBodyEdits.js, shared
  // with a Custom Email Template's editor (#608).
  const applyEdit = useCallback((patch) => setState((s) => (
    'body' in patch ? { ...s, body: patch.body, error: null, notice: null } : { ...s, notice: patch.notice }
  )), []);
  const { format, insertImage, insertPlaceholder } = useMessageBodyEdits(state.body, applyEdit);

  // Templates ▾: a saved template's words put in (`apply_template`).
  const pickTemplate = useCallback((template) => setState((s) => {
    const r = applyMessageTemplate({ subject: s.subject, body: s.body }, template);
    if (r.refusal) return { ...s, notice: r.refusal.message };
    return {
      ...s, subject: r.subject, body: r.body, error: null, notice: null,
    };
  }), []);

  // One file's upload, from the start: a first attempt or a Try again.
  // Resolves true when it is on the message.
  const start = useCallback((key, file, seed) => {
    const controller = new AbortController();
    going.current.set(key, controller);
    const mine = (x) => x.active && x.seed === seed && x.attachments.some((a) => a.key === key);
    return upload({
      // The queued message's own id, which is where the sender looks — not
      // the bare seed it is made from.
      messageId: teacherMessageId(seed),
      file,
      signal: controller.signal,
      onProgress: ({ transferred }) => setState((x) => (mine(x) ? {
        ...x, attachments: x.attachments.map((a) => (a.key === key ? { ...a, transferred } : a)),
      } : x)),
    }).then(
      (done) => {
        if (going.current.get(key) === controller) going.current.delete(key);
        // Removed, or the composer closed, while it went up: not this
        // message's any more.
        if (!mine(current.current)) {
          if (discard && done?.path) discard(done.path);
          return false;
        }
        picked.current.delete(key);
        setState((x) => (mine(x) ? {
          ...x,
          attachments: x.attachments.map((a) => (a.key === key ? {
            ...a, name: done.name, entry: done.entry, path: done.path, transferred: a.size, status: 'ready', message: null,
          } : a)),
        } : x));
        return true;
      },
      (err) => {
        if (going.current.get(key) === controller) going.current.delete(key);
        // Cancelled: it was removed, and says nothing.
        if (err?.cancelled) return false;
        setState((x) => (mine(x) ? {
          ...x,
          attachments: x.attachments.map((a) => (a.key === key ? {
            ...a, status: 'failed', transferred: 0, message: err?.message || composeWord('attach_failed'),
          } : a)),
        } : x));
        return false;
      },
    );
  }, [upload, discard]);

  // Attach Files (#605): one picked file, checked and uploaded. Resolves
  // true when it is on the message, false when it was refused or failed —
  // refused, `notice` says why; failed, its row does.
  const attachFile = useCallback(async (file) => {
    const s = current.current;
    if (!s.active || !file) return false;
    // A failed file still counts towards the bounds, since Try again would
    // put it back — as on both phones.
    const refusal = attachmentRefusal(file.name, file.size, s.attachments.map((a) => a.size));
    if (refusal) {
      setState((x) => ({ ...x, notice: refusal.message }));
      return false;
    }
    if (!upload) {
      setState((x) => ({ ...x, notice: composeWord('attach_failed') }));
      return false;
    }
    fileKeys += 1;
    const key = `att${fileKeys}`;
    const item = {
      key, name: file.name || 'file', size: Number(file.size) || 0, transferred: 0, status: 'uploading', entry: null, path: null, message: null,
    };
    picked.current.set(key, file);
    setState((x) => ({ ...x, attachments: [...x.attachments, item], notice: null, error: null }));
    return start(key, file, s.seed);
  }, [upload, start]);

  // Try again on a failed file: the same bytes, a fresh authorisation.
  const retryFile = useCallback((key) => {
    const s = current.current;
    const file = picked.current.get(key);
    if (!s.active || !file || !s.attachments.some((a) => a.key === key && a.status === 'failed')) {
      return Promise.resolve(false);
    }
    setState((x) => ({
      ...x,
      attachments: x.attachments.map((a) => (a.key === key ? {
        ...a, status: 'uploading', transferred: 0, message: null,
      } : a)),
      error: null,
    }));
    return start(key, file, s.seed);
  }, [start]);

  // A file taken off the message: its upload cancelled, or its bytes
  // deleted — and bytes that land despite the cancel are deleted when they
  // do (`start`).
  const detachFile = useCallback((key) => {
    const gone = current.current.attachments.find((a) => a.key === key);
    setState((x) => ({ ...x, attachments: x.attachments.filter((a) => a.key !== key), error: null }));
    forget(key);
    if (discard && gone?.path) discard(gone.path);
  }, [discard]);

  // What another surface wants said under the message: a template saved.
  const say = useCallback((notice) => setState((s) => ({ ...s, notice })), []);

  // `Select...`: the people an entry names, added to those already on To;
  // Clear Selection empties it (#600).
  const add = useCallback((choice) => setState((s) => ({
    ...s,
    to: choice?.key === clearSelectionKey() ? [] : composeRecipients([...s.to, ...(choice?.to || [])]).to,
    error: null,
  })), []);

  const remove = useCallback((address) => setState((s) => ({
    ...s, to: s.to.filter((r) => r.address !== address), error: null,
  })), []);

  const myself = useMemo(() => myselfRecipient(myEmail), [myEmail]);

  const submit = useCallback(async () => {
    const {
      active, seed, subject, body, extra, ccMe, attachments, from, later, date, time,
    } = state;
    if (!active || !send) return false;
    // A file still going up is not on the message yet; sending without it
    // would be a message other than the one the teacher sees.
    if (attachments.some((a) => a.status === 'uploading')) {
      setState((s) => ({ ...s, error: composeWord('attach_wait') }));
      return false;
    }
    // One that did not finish is not on the message either, and the teacher
    // decides: Try again, or remove it.
    if (attachments.some((a) => a.status === 'failed')) {
      setState((s) => ({ ...s, error: composeWord('attach_not_uploaded') }));
      return false;
    }
    const recipients = composeRecipients(state.to, extra);
    if (recipients.invalid.length) {
      setState((s) => ({ ...s, error: composeWords().extraInvalid }));
      return false;
    }
    // Send later (#599): the date and time on the profile's clock, as an
    // instant, which must be after now and within a year.
    let sendAt = null;
    if (later) {
      sendAt = instantOfWallClockIn(date, time, timeZone);
      const refusal = sendAtRefusal(sendAt ?? Number.NaN, now());
      if (refusal) {
        setState((s) => ({ ...s, error: refusal }));
        return false;
      }
    }
    const write = teacherMessageWrite({
      seed,
      to: recipients.to,
      cc: ccMe && myself ? [myself] : [],
      subject,
      body,
      familyId,
      studentId,
      sendAt,
      sendAs: from,
      attachments: settledUploads(attachments).map((a) => a.entry),
      studentIds: reachedFor ? reachedFor(recipients.to) : [],
    });
    if (write.refusal) {
      setState((s) => ({ ...s, error: write.refusal }));
      return false;
    }
    setState((s) => ({ ...s, sending: true, error: null }));
    return send(write).then(
      () => {
        setState(CLOSED);
        return true;
      },
      (err) => {
        // The queue refused it outright (a lapsed studio, the rules); a write
        // that is merely owed settles as sent and is the queue's to deliver.
        setState((s) => ({ ...s, sending: false, error: err?.message || String(err) }));
        return false;
      },
    );
  }, [state, send, familyId, studentId, myself, timeZone, now, reachedFor]);

  return {
    ...state,
    open,
    forward,
    close,
    change,
    add,
    remove,
    format,
    insertImage,
    insertPlaceholder,
    pickTemplate,
    attachFile,
    retryFile,
    detachFile,
    canAttach: Boolean(upload),
    say,
    submit,
    isOpen: state.active,
    canCcMe: Boolean(myself),
    fromChoices,
  };
}
